"use client"

import { useState, useEffect, useRef } from "react"
import { HelpCircle } from "lucide-react"

const COLOR_FAMILIES = [
  // Neutrals - Wall-friendly colors at the top
  { id: "warm_white", name: "Warm Whites", examples: ["#F4EFE6", "#F7F2EA"] },
  { id: "cool_white", name: "Cool Whites", examples: ["#F8F9FA", "#F1F3F4"] },
  { id: "griege", name: "Griege", examples: ["#D4CFC0", "#C9C3B8"] },
  { id: "cream", name: "Cream", examples: ["#FDF6E3", "#F5E6D3"] },
  { id: "soft_gray", name: "Soft Gray", examples: ["#E5E7EB", "#D1D5DB"] },
  { id: "medium_gray", name: "Medium Gray", examples: ["#9CA3AF", "#6B7280"] },
  { id: "dark_gray", name: "Dark Gray", examples: ["#4B5563", "#374151"] },
  { id: "black", name: "Black", examples: ["#1F2937", "#111827"] },
  { id: "mushroom", name: "Mushroom", examples: ["#C7B299", "#B8A082"] },
  { id: "linen", name: "Linen", examples: ["#FAF0E6", "#F5E6D3"] },

  // Reds
  { id: "crimson", name: "Crimson", examples: ["#DC143C", "#B91C3C"] },
  { id: "burgundy", name: "Burgundy", examples: ["#800020", "#722F37"] },
  { id: "blush_pink", name: "Blush Pink", examples: ["#F4C2C2", "#E8A5A5"] },

  // Red-Oranges
  { id: "coral", name: "Coral", examples: ["#FF7F50", "#F08080"] },
  { id: "terracotta", name: "Terracotta", examples: ["#C06E52", "#B85E48"] },

  // Oranges
  { id: "rust_orange", name: "Rust Orange", examples: ["#B7410E", "#A0390D"] },
  { id: "peach", name: "Peach", examples: ["#FFCBA4", "#FFB07A"] },

  // Yellow-Oranges
  { id: "amber", name: "Amber", examples: ["#FFBF00", "#FFC000"] },

  // Yellows
  { id: "golden_yellow", name: "Golden Yellow", examples: ["#FFD700", "#F4D03F"] },

  // Yellow-Greens
  { id: "chartreuse", name: "Chartreuse", examples: ["#7FFF00", "#9ACD32"] },

  // Greens
  { id: "sage_green", name: "Sage Green", examples: ["#9CAF88", "#87A96B"] },
  { id: "forest_green", name: "Forest Green", examples: ["#355E3B", "#2F5233"] },
  { id: "olive", name: "Olive", examples: ["#808000", "#6B8E23"] },
  { id: "mint", name: "Mint", examples: ["#98FB98", "#AAFFC3"] },

  // Blue-Greens
  { id: "teal", name: "Teal", examples: ["#008080", "#20B2AA"] },
  { id: "turquoise", name: "Turquoise", examples: ["#40E0D0", "#48D1CC"] },

  // Blues
  { id: "powder_blue", name: "Powder Blue", examples: ["#B6D7FF", "#A3C4F3"] },
  { id: "navy_blue", name: "Navy Blue", examples: ["#1B2951", "#0F1B3C"] },
  { id: "royal_blue", name: "Royal Blue", examples: ["#4169E1", "#0041C2"] },

  // Blue-Purples
  { id: "indigo", name: "Indigo", examples: ["#4B0082", "#6A0DAD"] },

  // Purples
  { id: "lavender", name: "Lavender", examples: ["#C8A2C8", "#B794B7"] },
  { id: "eggplant", name: "Eggplant", examples: ["#614051", "#483248"] },

  // Red-Purples
  { id: "magenta", name: "Magenta", examples: ["#FF00FF", "#DA70D6"] },
]

interface ColorPreferences {
  preferHues: string[]
  avoidHues: string[]
  moodAxis: "calm" | "balanced" | "bold"
  intensity: "muted" | "medium" | "vibrant"
  accentWall: boolean
}

interface RoomColorSelectionsProps {
  onColorDetailsChange?: (
    colorDetails: string,
    preferHues: string[],
    avoidHues: string[],
    mood: "calm" | "balanced" | "bold",
    intensity: "muted" | "medium" | "vibrant",
    accentWall: boolean,
  ) => void
  currentStep?: number
  onStepChange?: (step: number) => void
  selectedPalette?: string
  setSelectedPalette?: (palette: string) => void
  selectedPrimaryColor?: string
  setSelectedPrimaryColor?: (color: string) => void
  initialMood?: "calm" | "balanced" | "bold"
  initialIntensity?: "muted" | "medium" | "vibrant"
  initialAccentWall?: boolean
  initialPreferHues?: string[]
  initialAvoidHues?: string[]
}

const Button = ({ children, onClick, disabled, variant = "default", className = "", ...props }: any) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-md font-medium transition-colors ${
      variant === "outline"
        ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        : "bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50"
    } ${className}`}
    {...props}
  >
    {children}
  </button>
)

const Card = ({ children, className = "", ...props }: any) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`} {...props}>
    {children}
  </div>
)

const Switch = ({ checked, onCheckedChange, ...props }: any) => (
  <button
    onClick={() => onCheckedChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? "bg-teal-500" : "bg-gray-200"
    }`}
    {...props}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
)

export default function RoomColorSelections({
  onColorDetailsChange,
  currentStep = 1,
  onStepChange,
  selectedPalette,
  setSelectedPalette,
  selectedPrimaryColor,
  setSelectedPrimaryColor,
  initialMood = "balanced",
  initialIntensity = "medium",
  initialAccentWall = false,
  initialPreferHues = [],
  initialAvoidHues = [],
}: RoomColorSelectionsProps) {
  const [preferences, setPreferences] = useState<ColorPreferences>({
    preferHues: initialPreferHues,
    avoidHues: initialAvoidHues,
    moodAxis: initialMood,
    intensity: initialIntensity,
    accentWall: initialAccentWall,
  })

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    if (hasInitializedRef.current) {
      return
    }

    // Only set if we have actual initial values to restore
    if (
      initialPreferHues.length > 0 ||
      initialAvoidHues.length > 0 ||
      initialMood !== "balanced" ||
      initialIntensity !== "medium" ||
      initialAccentWall !== false
    ) {
      setPreferences((prev) => ({
        ...prev,
        preferHues: initialPreferHues.length > 0 ? initialPreferHues : prev.preferHues,
        avoidHues: initialAvoidHues.length > 0 ? initialAvoidHues : prev.avoidHues,
        moodAxis: initialMood,
        intensity: initialIntensity,
        accentWall: initialAccentWall,
      }))
      hasInitializedRef.current = true
    }
  }, [initialMood, initialIntensity, initialAccentWall, initialPreferHues, initialAvoidHues])

  const callbackRef = useRef(onColorDetailsChange)
  callbackRef.current = onColorDetailsChange

  const generateColorDetailsString = (prefs: ColorPreferences): string => {
    const preferHues = Array.isArray(prefs.preferHues) ? prefs.preferHues : []
    const avoidHues = Array.isArray(prefs.avoidHues) ? prefs.avoidHues : []

    const preferredColors = preferHues
      .map((id) => {
        const family = COLOR_FAMILIES.find((f) => f.id === id)
        return family ? `"${family.name}"` : id
      })
      .join(", ")

    const avoidedColors = avoidHues
      .map((id) => {
        const family = COLOR_FAMILIES.find((f) => f.id === id)
        return family ? `"${family.name}"` : id
      })
      .join(", ")

    let colorDetails = `Preferred colors (in no order of priority): ${preferredColors}`

    if (avoidedColors) {
      colorDetails += `, Colors to avoid: ${avoidedColors}`
    }

    colorDetails += `, Mood: ${prefs.moodAxis.charAt(0).toUpperCase() + prefs.moodAxis.slice(1)}`
    colorDetails += `, Intensity: ${prefs.intensity.charAt(0).toUpperCase() + prefs.intensity.slice(1)}`
    colorDetails += `, Include Accent Wall: ${prefs.accentWall ? "Yes" : "No"}`

    return colorDetails
  }

  const updatePreferences = (updates: Partial<ColorPreferences>) => {
    setPreferences((prev) => ({ ...prev, ...updates }))
  }

  const toggleColorFamily = (familyId: string, type: "prefer" | "avoid") => {
    const key = type === "prefer" ? "preferHues" : "avoidHues"
    const otherKey = type === "prefer" ? "avoidHues" : "preferHues"

    setPreferences((prev) => {
      const current = prev[key]
      const other = prev[otherKey]

      if (current.includes(familyId)) {
        return { ...prev, [key]: current.filter((id) => id !== familyId) }
      } else if (type === "prefer" && current.length >= 3) {
        return prev
      } else {
        return {
          ...prev,
          [key]: [...current, familyId],
          [otherKey]: other.filter((id) => id !== familyId),
        }
      }
    })
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return preferences.preferHues.length >= 1
      case 2:
        return true
      case 3:
        return true
      default:
        return false
    }
  }

  useEffect(() => {
    if (callbackRef.current) {
      const colorDetails = generateColorDetailsString(preferences)

      // Check if callback expects 6 parameters (CanvasDesignWizard) or 2 parameters (StartFromScratch/UploadRoom)
      if (callbackRef.current.length === 6) {
        // New signature: individual parameters
        callbackRef.current(
          colorDetails,
          preferences.preferHues,
          preferences.avoidHues,
          preferences.moodAxis,
          preferences.intensity,
          preferences.accentWall,
        )
      } else {
        // Old signature: colorDetails and preferences object
        callbackRef.current(colorDetails, {
          preferHues: preferences.preferHues,
          avoidHues: preferences.avoidHues,
        })
      }
    }
  }, [preferences])

  const renderColorFamilyChip = (family: (typeof COLOR_FAMILIES)[0], isSelected: boolean, onClick: () => void) => (
    <button
      key={family.id}
      onClick={onClick}
      className={`p-3 rounded-lg border-2 transition-all ${
        isSelected ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="flex items-center space-x-3">
        <div className="flex space-x-1">
          {family.examples.map((color, idx) => (
            <div key={idx} className="w-6 h-6 rounded-full border border-gray-200" style={{ backgroundColor: color }} />
          ))}
        </div>
        <span className="text-sm font-medium">{family.name}</span>
      </div>
    </button>
  )

  const renderSegmentedControl = (options: string[], value: string, onChange: (value: string) => void) => (
    <div className="flex bg-gray-100 rounded-lg p-1">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            value === option ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
          }`}
        >
          {option.charAt(0).toUpperCase() + option.slice(1)}
        </button>
      ))}
    </div>
  )

  const renderTooltip = (text: string) => (
    <div className="group relative inline-block ml-2">
      <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-64 whitespace-normal z-10">
        {text}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
      </div>
    </div>
  )

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-2">Pick 3 you love</h2>
              <p className="text-gray-600">
                Choose your favorite color families - these will be the main colors featured in your design options
                <br />
                Choose Up To 3
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {COLOR_FAMILIES.map((family) =>
                renderColorFamilyChip(family, preferences.preferHues.includes(family.id), () =>
                  toggleColorFamily(family.id, "prefer"),
                ),
              )}
            </div>
            <div className="text-center text-sm text-gray-500">{preferences.preferHues.length}/3 selected</div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-2">Anything to avoid?</h2>
              <p className="text-gray-600">Select color families you'd prefer not to use (optional)</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {COLOR_FAMILIES.filter((f) => !preferences.preferHues.includes(f.id)).map((family) =>
                renderColorFamilyChip(family, preferences.avoidHues.includes(family.id), () =>
                  toggleColorFamily(family.id, "avoid"),
                ),
              )}
            </div>
            <div className="text-center text-sm text-gray-500">
              {preferences.avoidHues.length > 0
                ? `${preferences.avoidHues.length} color${preferences.avoidHues.length === 1 ? "" : "s"} to avoid`
                : "No colors to avoid selected"}
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-semibold mb-2">How should it feel?</h2>
              <p className="text-gray-600">Set the mood and energy for your space</p>
            </div>

            <div className="space-y-6">
              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium mb-2">Mood</label>
                  {renderTooltip(
                    "Calm: Relaxing, peaceful colors that help you unwind. Balanced: Versatile colors that work well in any situation. Bold: Eye-catching colors that make a strong impression.",
                  )}
                </div>
                {renderSegmentedControl(["calm", "balanced", "bold"], preferences.moodAxis, (value) =>
                  updatePreferences({ moodAxis: value as any }),
                )}
              </div>

              <div>
                <div className="flex items-center">
                  <label className="block text-sm font-medium mb-2">Intensity</label>
                  {renderTooltip(
                    "Muted: Soft, gentle colors that blend into the background. Medium: Colors with moderate richness - not too soft, not too bold. Vibrant: Rich, lively colors that stand out and energize the space.",
                  )}
                </div>
                {renderSegmentedControl(["muted", "medium", "vibrant"], preferences.intensity, (value) =>
                  updatePreferences({ intensity: value as any }),
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium">Accent Wall</label>
                  <p className="text-xs text-gray-500">Include a bold accent wall option</p>
                </div>
                <Switch
                  checked={preferences.accentWall}
                  onCheckedChange={(checked) => updatePreferences({ accentWall: checked })}
                />
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return renderCurrentStep()
}
